import React, { useEffect, useState } from 'react';
import api, { isLimitError } from '../../api/api';
import { useCreateContext } from '../../product/CreateContext';
import { parseAiJson } from '../../utils/parseAiJson';
import RepurposeMenu from '../../components/RepurposeMenu';
import Icon from '../../components/Icon';
import { CopyButton, ErrorBanner, Field, GenerateButton, formGrid } from './generatorUi';
import { useCreateFlow } from './flow';

// Character limits per placement, so copy can be pasted straight into the ad manager
const PLATFORMS = {
    meta: { label: 'Meta (Facebook & Instagram)', limits: { headline: 40, primaryText: 125, description: 30 } },
    google: { label: 'Google Search', limits: { headline: 30, description: 90 } },
    tiktok: { label: 'TikTok', limits: { primaryText: 100 } },
    linkedin: { label: 'LinkedIn', limits: { headline: 70, primaryText: 150 } },
};

const FIELD_LABELS = { headline: 'Headline', primaryText: 'Primary text', description: 'Description' };

const AD_FIELDS = ['angle', 'headline', 'primaryText', 'description', 'cta'];

// The variant the post uses, with what Review needs to show it
const toAd = (variant, placement) => ({
    ...AD_FIELDS.reduce((acc, field) => ({ ...acc, [field]: variant[field] || '' }), {}),
    placement,
    placementLabel: PLATFORMS[placement].label,
    fields: Object.keys(PLATFORMS[placement].limits),
    limits: PLATFORMS[placement].limits,
});

const AdCopyScreen = () => {
    const { product, promptContext, seed } = useCreateContext();
    const { flow, report } = useCreateFlow();
    const [platform, setPlatform] = useState(() => (PLATFORMS[flow.ad?.placement] ? flow.ad.placement : 'meta'));
    const [angle, setAngle] = useState(seed?.hook || seed?.topic || '');
    const [offer, setOffer] = useState(product?.offer || '');
    // A variant picked before a reload comes back on its own
    const [variants, setVariants] = useState(() => (flow.ad ? [flow.ad] : []));
    // The placement the variants were written for (the picker may have changed since)
    const [variantPlatform, setVariantPlatform] = useState(() => (PLATFORMS[flow.ad?.placement] ? flow.ad.placement : 'meta'));
    const [chosen, setChosen] = useState(flow.ad ? 0 : -1);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    const limits = PLATFORMS[platform].limits;
    const fields = Object.keys(limits);
    const variantLimits = PLATFORMS[variantPlatform].limits;
    const variantFields = Object.keys(variantLimits);

    // The chosen variant is the post's ad copy in the next steps
    useEffect(() => {
        const variant = variants[chosen];
        report('copy', { ad: variant ? toAd(variant, variantPlatform) : null }, Boolean(variant));
    }, [variants, chosen, variantPlatform]); // eslint-disable-line react-hooks/exhaustive-deps

    // Edits made in Review & post show here too
    useEffect(() => {
        if (!flow.ad) return;
        setVariants(previous => {
            const variant = previous[chosen];
            if (!variant || AD_FIELDS.every(field => (variant[field] || '') === (flow.ad[field] || ''))) return previous;
            return previous.map((item, index) => (index === chosen ? { ...item, ...AD_FIELDS.reduce((acc, field) => ({ ...acc, [field]: flow.ad[field] || '' }), {}) } : item));
        });
    }, [flow.ad]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleGenerate = async () => {
        const prompt = `You are a performance marketer who writes direct-response ad copy.
Write 3 distinct ad variants for ${PLATFORMS[platform].label}, each testing a different angle (e.g. pain, outcome, proof).
${angle.trim() ? `Angle or idea to build on: ${angle.trim()}\n` : ''}${offer.trim() ? `Offer to promote: ${offer.trim()}\n` : ''}
What you know about the product, who it's for and the brand voice (write for this person, in this voice):
${promptContext}

Hard character limits: ${fields.map(field => `${field} ${limits[field]}`).join(', ')}. Count characters and stay under them.
Use the customer's own words where you can. No clickbait, no claims the product facts don't support.

Respond ONLY with JSON in this shape, no other text:
{"variants": [{"angle": "short name of the angle", ${fields.map(field => `"${field}": "..."`).join(', ')}, "cta": "Learn more"}]}`;

        setError('');
        setIsLoading(true);
        try {
            const parsed = parseAiJson(await api.getChatCompletion(prompt));
            const list = (Array.isArray(parsed) ? parsed : parsed?.variants || [])
                .filter(item => item && typeof item === 'object')
                .map(item => ({
                    angle: String(item.angle || ''),
                    cta: String(item.cta || ''),
                    ...fields.reduce((acc, field) => ({ ...acc, [field]: String(item[field] || '').trim() }), {}),
                }));
            if (list.length === 0) throw new Error('No variants in response');
            setVariants(list);
            setVariantPlatform(platform);
            // The first one is used until another is picked
            setChosen(0);
        } catch (e) {
            if (isLimitError(e)) return;
            setError("Couldn't write ad copy. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    const updateVariant = (index, field, value) => {
        setVariants(prev => prev.map((variant, i) => (i === index ? { ...variant, [field]: value } : variant)));
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <ErrorBanner message={error} />
            <div className="ls-card" style={{ gap: 16 }}>
                <div className="ls-card-title">Ad brief</div>
                <div style={formGrid}>
                    <Field label="Placement">
                        <select className="ls-select" value={platform} onChange={(e) => setPlatform(e.target.value)}>
                            {Object.entries(PLATFORMS).map(([id, option]) => <option key={id} value={id}>{option.label}</option>)}
                        </select>
                    </Field>
                    <Field label="Offer">
                        <input className="ls-input" value={offer} onChange={(e) => setOffer(e.target.value)} placeholder="e.g. First week of Pro free" />
                    </Field>
                </div>
                <Field label="Angle or idea (optional)">
                    <input className="ls-input" value={angle} onChange={(e) => setAngle(e.target.value)} placeholder="e.g. Back-to-routine after the holidays" />
                </Field>
                <GenerateButton isLoading={isLoading} onClick={handleGenerate} label={variants.length ? 'Write new variants' : 'Write ad copy'} loadingLabel="Writing..." secondary={variants.length > 0} />
            </div>

            {variants.length > 0 && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 20 }}>
                    {variants.map((variant, index) => (
                        <div className={`ls-card ls-cflow-variant${index === chosen ? ' is-chosen' : ''}`} key={index} style={{ gap: 12 }}>
                            <div className="ls-card-header">
                                <span className="ls-card-title">{variant.angle || `Variant ${index + 1}`}</span>
                                <CopyButton text={[...variantFields.map(field => variant[field]), variant.cta].filter(Boolean).join('\n\n')} />
                            </div>
                            <button
                                type="button"
                                className={`ls-btn ls-btn-pill ls-cflow-choose${index === chosen ? ' is-on' : ''}`}
                                aria-pressed={index === chosen}
                                onClick={() => setChosen(index)}
                            >
                                <Icon name={index === chosen ? 'check_circle' : 'radio_button_unchecked'} size={16} filled={index === chosen} />
                                {index === chosen ? 'Using this one' : 'Use this one'}
                            </button>
                            {variantFields.map(field => {
                                const value = variant[field] || '';
                                const over = value.length > variantLimits[field];
                                return (
                                    <Field
                                        key={field}
                                        label={FIELD_LABELS[field]}
                                        hint={<span style={{ color: over ? 'var(--danger-text)' : undefined }}>{value.length} / {variantLimits[field]}</span>}
                                    >
                                        <textarea
                                            className="ls-textarea is-compact"
                                            rows={field === 'primaryText' ? 3 : 1}
                                            value={value}
                                            onChange={(e) => updateVariant(index, field, e.target.value)}
                                        />
                                    </Field>
                                );
                            })}
                            {variant.cta && <span className="ls-chip" style={{ alignSelf: 'flex-start' }}>CTA: {variant.cta}</span>}
                            <div style={{ display: 'flex', marginTop: 'auto' }}>
                                <RepurposeMenu
                                    source={{
                                        platform: PLATFORMS[variantPlatform].label,
                                        contentType: 'Ad',
                                        hook: variant.headline || variant.primaryText,
                                        text: [...variantFields.map(field => variant[field]), variant.cta].filter(Boolean).join('\n\n'),
                                    }}
                                    exclude={['ad']}
                                />
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default AdCopyScreen;
